import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject, Subscription, interval } from 'rxjs';

import { dataService } from '@app/modules/data';
import { Place } from '@app/modules/model/place';
import { User } from '@app/modules/model/user';

/**
 * Bloqueo por camarero (Fase 2b) — 100% front, SIN tocar la sesión/auth.
 *
 * Monitoriza inactividad (click/tecla/touch globales). Cuando pasa el tiempo
 * configurado sin actividad, BLOQUEA: limpia la identidad (data.CurrentWaiter) y
 * pide PIN. El PIN identifica a un camarero (place.waiters por pin) y se fija
 * como data.CurrentWaiter -> atribución + permisos (via ui-place.get user()).
 *
 * Habilitado solo si el local tiene la opción activada Y >=1 camarero con PIN.
 * El tiempo de bloqueo es configurable (optionWaiterLockTime, por dispositivo).
 */
@Injectable({ providedIn: 'root' })
export class waiterLockService {
    private _place: Place = null;
    private _enabled = false;
    private _lockSeconds = 60;
    private _lastActivity = Date.now();
    private _tick: Subscription = null;
    private _locked = false;
    private _modalActive = false;   // hay un modal de PIN abierto (manual o bloqueo)
    private _paymentCount = 0;      // nº de cobros con cajón/datáfono EN CURSO (contador, no bool: soporta modales solapados)
    private _activityHandler = () => this.UpdateActivity();

    private _onCountdown = new BehaviorSubject<number>(-1);
    public OnCountdown = this._onCountdown.asObservable();
    private _onLockRequired = new Subject<void>();
    public OnLockRequired = this._onLockRequired.asObservable();
    // Se emite cuando el bloqueo queda DESACTIVADO estando activo (opción off o se
    // quitaron todos los PIN): la vista debe cerrar el modal de PIN si está abierto
    // (si no, quedaría un modal de bloqueo que ningún PIN puede satisfacer).
    private _onDisabled = new Subject<void>();
    public OnDisabled = this._onDisabled.asObservable();
    // Se emite al IDENTIFICARSE (cambio de camarero por PIN): las vistas con
    // operaciones privilegiadas abiertas (p.ej. el backoffice del cajón) deben
    // cerrarlas, porque el nuevo camarero puede no tener permiso.
    private _onIdentified = new Subject<void>();
    public OnIdentified = this._onIdentified.asObservable();

    constructor(private data: dataService){
    }

    get IsEnabled(): boolean {
        return this._enabled;
    }

    get IsLocked(): boolean {
        return this._locked;
    }

    get HasActiveWaiter(): boolean {
        return this.data.HasActiveWaiter;
    }

    /** Arranca el bloqueo para un local: lee config, engancha listeners y timer.
     *  Idempotente (Stop + re-Start). Si al arrancar no hay camarero
     *  identificado, pide PIN ya. */
    Start(place: Place){
        this.Stop();
        this._place = place;
        this._refreshConfig();
        if (!this._enabled){
            return;
        }

        this._lastActivity = Date.now();
        document.addEventListener('click', this._activityHandler, true);
        document.addEventListener('keydown', this._activityHandler, true);
        document.addEventListener('touchstart', this._activityHandler, true);
        this._tick = interval(1000).subscribe(() => this._onTick());

        if (!this.HasActiveWaiter){
            this._requireLock();
        }
    }

    Stop(){
        if (this._tick){
            this._tick.unsubscribe();
            this._tick = null;
        }
        document.removeEventListener('click', this._activityHandler, true);
        document.removeEventListener('keydown', this._activityHandler, true);
        document.removeEventListener('touchstart', this._activityHandler, true);
        this._locked = false;
        // Resetear el flag de modal: Stop() puede ejecutarse con un modal de PIN
        // abierto (p.ej. al destruirse la pantalla). Como el servicio es singleton,
        // si quedara en true el siguiente Start() tendría el timer permanentemente
        // pausado (_onTick saldría siempre) y no bloquearía nunca.
        this._modalActive = false;
        this.data.WaiterLockActive = false;     // sin bloqueo -> EffectiveUser normal
        this._onCountdown.next(-1);
        // Limpiar la identidad: si no, al cambiar de local la singleton dataService
        // seguiría con el camarero del local anterior y HasActiveWaiter saltaría el
        // PIN obligatorio (y aplicaría la identidad equivocada).
        this.data.CurrentWaiter = null;
    }

    /** Reevalúa la config SIN resetear el temporizador si ya estaba activo. Se
     *  llama en cada refresh del local: cubre activar/desactivar la opción,
     *  cambiar el tiempo, y altas/bajas de PIN (quitar el último PIN desactiva y
     *  avisa para cerrar el modal). */
    Recheck(place: Place){
        this._place = place;
        let _wasEnabled = this._enabled;
        this._refreshConfig();

        if (!this._enabled){
            if (_wasEnabled){
                this.Stop();
                this._onDisabled.next();    // cerrar el modal de PIN si está abierto
            }
            // Sin bloqueo también puede haber camarero identificado (por pulsera): si
            // ya no es un camarero válido con credencial (borrado, pulsera quitada), se
            // suelta la identidad para no atribuir ni evaluar permisos con un user que
            // ya no casa en place.waiters. Por Identify(null), que emite OnIdentified:
            // el cajón/backoffice cierran sus modales privilegiados con ese evento.
            else if (this.HasActiveWaiter && !this._currentWaiterStillValid()){
                this.Identify(null);
            }
            return;
        }

        if (!_wasEnabled){
            this.Start(place);
            return;
        }

        // Ya estaba activo: _refreshConfig ya actualizó _lockSeconds; el timer sigue.
        // Pero si el camarero identificado YA NO existe como camarero válido con
        // PIN (lo han borrado), su user ya no casa en place.waiters -> Allow* caería
        // a permisos de OWNER (escalada). Volvemos a pedir PIN.
        if (!this._locked && this.HasActiveWaiter && !this._currentWaiterStillValid()){
            this._requireLock();
        }
    }

    // La pulsera solo cuenta como credencial donde se puede leer: lector activado en
    // ESTE dispositivo (pestaña Dispositivo, optionRfidReader).
    private _rfidUsable(): boolean {
        return !!this._place && !!this._place.HasRfidReader;
    }

    private _currentWaiterStillValid(): boolean {
        let _u = this.data.CurrentWaiter;
        if (!_u || !this._place){
            return false;
        }
        // Camarero con credencial (PIN o PULSERA), o el OWNER del local con credencial
        // (el owner no es STAFF). Sin la pulsera aquí, un camarero identificado con
        // ella pero sin PIN caía al bloqueo en el siguiente refresh del local.
        let _rfid = this._rfidUsable();
        if (this._place.user && (this._place.user.has_pin || (_rfid && this._place.user.has_rfid)) && (this._place.user == _u)){
            return true;
        }
        return this._place.waiters.some(w => w.IsValid && (w.has_pin || (_rfid && w.has_rfid)) && (w.user == _u));
    }

    // Lee una opción de config POR DISPOSITIVO (formato 'device;value|device2;...'
    // de GetConfigList, igual que _hasTerminal en ui-tab-device). Devuelve el
    // valor de ESTE dispositivo (string), true (presente sin valor) o false.
    private _terminalOption(key: string): any {
        let _raw = (this._place ? this._place.GetConfigList(key) : '') || '';
        for(let _dev of _raw.split('|')){
            if (!_dev || !_dev.trim()){
                continue;
            }
            let _parts = _dev.split(';');
            if (_parts[0] == this.data.device){
                return (_parts.length == 2) ? _parts[1] : true;
            }
        }
        return false;
    }

    private _refreshConfig(){
        let _opt = this._terminalOption('optionWaiterLock');     // true/'1' si activo
        // Habilitado si hay >=1 camarero con credencial (PIN, o pulsera SI este dispositivo
        // tiene lector) O el owner del local la tiene (si no, nadie podría desbloquear:
        // con solo pulseras y sin lector aquí, el bloqueo no cancelable dejaría el TPV
        // sin forma de identificarse).
        let _rfid = this._rfidUsable();
        let _hasPin = this._place ? (this._place.waiters.some(w => w.IsValid && (w.has_pin || (_rfid && w.has_rfid)))
            || !!(this._place.user && (this._place.user.has_pin || (_rfid && this._place.user.has_rfid)))) : false;
        this._enabled = ((_opt === true) || (_opt == '1')) && _hasPin;

        // 0 = NUNCA se bloquea por inactividad: el camarero identificado se mantiene hasta
        // que alguien pulse "cambiar camarero" (LockNow). Sigue pidiendo PIN al entrar.
        let _t = parseInt(this._terminalOption('optionWaiterLockTime') as any);
        this._lockSeconds = (_t === 0) ? 0 : ((_t && (_t >= 5)) ? _t : 60);

        // Publicar el estado del bloqueo en data: el usuario EFECTIVO (data.
        // EffectiveUser) devuelve el principal bloqueado cuando el bloqueo está
        // activo y no hay camarero identificado, para que TODOS los consumidores
        // (ui-place, ticket, producto) denieguen permisos de forma consistente.
        this.data.WaiterLockActive = this._enabled;
    }

    UpdateActivity(){
        this._lastActivity = Date.now();
        if (this._onCountdown.value !== -1){
            this._onCountdown.next(-1);
        }
    }

    /** La vista avisa cuando hay un modal de PIN abierto (manual o de bloqueo): se
     *  pausa el temporizador y se oculta la cuenta atrás. Al cerrarse se reanuda
     *  reiniciando la inactividad (igual que new_camareros con _isLockModalActive). */
    SetModalActive(active: boolean){
        this._modalActive = active;
        if (active){
            this._onCountdown.next(-1);     // ocultar la cuenta atrás
        }
        else {
            this.UpdateActivity();          // reanudar con el contador a cero
        }
    }

    /** Cobro con cajón inteligente / datáfono en curso: mientras dure, la UI NO se
     *  bloquea por inactividad (ver _onTick). Lo activa el modal de cobro al abrirse y
     *  lo limpia SIEMPRE al cerrarse (ngOnDestroy), así que el bloqueo solo queda
     *  suspendido durante el pago real, no indefinidamente. */
    SetPaymentActive(active: boolean){
        // Contador, no booleano: dos cobros solapados (p.ej. un modal libera _waitmodal
        // 100 ms tras present(), ver ui-view.ts) no deben desactivar la suspensión hasta
        // que se cierre el ÚLTIMO. Solo al llegar a 0 vuelve a correr el bloqueo.
        if (active){
            this._paymentCount++;
            this.UpdateActivity();          // reset del contador al entrar en el cobro
        }
        else if (this._paymentCount > 0){
            this._paymentCount--;
            if (this._paymentCount === 0){
                this.UpdateActivity();      // sale el último cobro: reanuda con el contador a cero
            }
        }
    }

    /** ¿Hay un modal de PIN abierto (manual/captura/bloqueo)? Lo usa el lector de
     *  códigos para no procesar escaneos durante la entrada de PIN (el numpad no
     *  tiene input de texto, así que el listener a nivel de documento seguía
     *  recibiendo las teclas y podía mutar la comanda). */
    get IsPinModalOpen(): boolean {
        return this._modalActive;
    }

    private _onTick(){
        if (!this._enabled || this._locked){
            return;
        }

        // COBRO con cajón/datáfono EN CURSO: no bloquear a mitad de pago. El operario
        // no hace clicks porque la máquina trabaja (mete el cliente el efectivo / el
        // datáfono procesa), así que mantenemos la sesión viva mientras dura el cobro.
        // Se corta AQUÍ, al principio del tick: así no se publica la cuenta atrás 5…1
        // durante el pago y el contador de inactividad se renueva en cada tick (no solo
        // al vencer), evitando que el bloqueo salte justo al cerrar un cobro largo.
        // Es una operación REAL y acotada (su propio timeout), y SIEMPRE se limpia al
        // cerrar el modal de cobro (SetPaymentActive(false) en su ngOnDestroy).
        if (this._paymentCount > 0){
            this.UpdateActivity();          // reset: no bloquear durante el cobro
            if (this._onCountdown.value !== -1){
                this._onCountdown.next(-1); // ocultar la cuenta atrás si estaba visible
            }
            return;
        }

        if (this._lockSeconds <= 0){
            return;     // sin bloqueo por inactividad (solo manual, ver LockNow)
        }

        let _elapsed = (Date.now() - this._lastActivity) / 1000;
        let _remaining = this._lockSeconds - _elapsed;

        if (_remaining <= 0){
            // El bloqueo SALTA aunque haya un modal cancelable abierto (cambio
            // manual / captura de PIN): si no, dejar ese modal abierto suspendía el
            // bloqueo indefinidamente y se podía cancelar para recuperar la sesión
            // sin PIN. OnLockRequired cierra el modal cancelable y abre el de bloqueo.
            this._requireLock();
        }
        // La cuenta atrás NO se dibuja sobre un modal abierto (_modalActive).
        else if (!this._modalActive && (_remaining <= 5)){
            this._onCountdown.next(Math.ceil(_remaining));
        }
        else if (this._onCountdown.value !== -1){
            this._onCountdown.next(-1);
        }
    }

    private _requireLock(){
        this._locked = true;
        this._onCountdown.next(-1);
        this.data.CurrentWaiter = null;     // limpia la identidad -> re-gatea la UI
        this._onLockRequired.next();
    }

    /** Bloqueo MANUAL (botón de camarero en la cabecera / "cambiar camarero"):
     *  mismo comportamiento que el bloqueo por inactividad — se limpia la identidad
     *  y el modal de PIN NO se puede cerrar sin identificarse. Antes abría un modal
     *  cancelable: pulsar fuera lo cerraba y seguía el camarero anterior. */
    LockNow(){
        if (!this._enabled || this._locked){
            return;
        }
        this._requireLock();
    }

    /** Identificado por PIN: fija el usuario efectivo (camarero u owner) y
     *  desbloquea. El modal resuelve el User server-side (ver ui-waiter-pin). */
    Identify(user: User){
        this.data.CurrentWaiter = user || null;
        this._locked = false;
        this.UpdateActivity();
        this._onIdentified.next();      // cambió el camarero: cerrar ops privilegiadas
    }
}
